import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, ArrowRight, Shield, Users, Building2, GraduationCap, 
  Mail, Settings, Plus, Trash2, Edit, Search, Download, Upload,
  BarChart3, Eye, RefreshCw, CheckCircle, XCircle, Loader2,
  TrendingUp, Briefcase, Globe, DollarSign, UserPlus, Building,
  MessageSquare, Send, FileDown, PieChart, Cpu, Activity, Clock
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [startups, setStartups] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [founders, setFounders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [mentorRegistrations, setMentorRegistrations] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [users, setUsers] = useState([]);
  const [pitchBookings, setPitchBookings] = useState([]);
  const [mlopsData, setMlopsData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationSummary, setNotificationSummary] = useState(null);
  
  // Search/filter states
  const [startupSearch, setStartupSearch] = useState("");
  const [founderSearch, setFounderSearch] = useState("");
  
  // Dialog states
  const [addStartupOpen, setAddStartupOpen] = useState(false);
  const [addMentorOpen, setAddMentorOpen] = useState(false);
  const [addFounderOpen, setAddFounderOpen] = useState(false);
  const [editStartupOpen, setEditStartupOpen] = useState(false);
  const [editMentorOpen, setEditMentorOpen] = useState(false);
  const [editFounderOpen, setEditFounderOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Nova chat state
  const [novaQuestion, setNovaQuestion] = useState("");
  const [novaResponse, setNovaResponse] = useState("");
  const [novaLoading, setNovaLoading] = useState(false);

  // Form states
  const [newStartup, setNewStartup] = useState({
    name: "", founders: "", industry: "", stage: "MVP", location: "Dubai, UAE",
    description: "", governance_score: 7, compliance_status: "Yellow", 
    risk_level: "Medium", ecosystem: "Climate Tech", cohort: "Cohort 1", mentor: ""
  });
  const [newMentor, setNewMentor] = useState({ name: "", expertise: "", bio: "", email: "" });
  const [newFounder, setNewFounder] = useState({ 
    first_name: "", last_name: "", email: "", company: "", title: "", industry: "Technology" 
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setRefreshing(true);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      const [statsRes, startupsRes, mentorsRes, foundersRes, companiesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/startups`),
        axios.get(`${API}/mentors`),
        axios.get(`${API}/founders`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/admin/analytics`)
      ]);
      setStats(statsRes.data);
      setStartups(startupsRes.data);
      setMentors(mentorsRes.data);
      setFounders(foundersRes.data);
      setCompanies(companiesRes.data);
      setAnalytics(analyticsRes.data);
      
      // Fetch approval data (requires auth)
      if (token) {
        try {
          const [mentorRegsRes, investorsRes, usersRes, pitchRes] = await Promise.all([
            axios.get(`${API}/mentor-registrations`, { headers }),
            axios.get(`${API}/investors`, { headers }),
            axios.get(`${API}/auth/users`, { headers }),
            axios.get(`${API}/pitch-bookings`, { headers })
          ]);
          setMentorRegistrations(mentorRegsRes.data);
          setInvestors(investorsRes.data);
          setUsers(usersRes.data);
          setPitchBookings(pitchRes.data);
          
          // Fetch MLOps data
          const mlopsRes = await axios.get(`${API}/mlops/dashboard`);
          setMlopsData(mlopsRes.data);
          
          // Fetch notifications
          try {
            const [notifRes, notifSummaryRes] = await Promise.all([
              axios.get(`${API}/admin/notifications`, { headers }),
              axios.get(`${API}/admin/notifications/summary`, { headers })
            ]);
            setNotifications(notifRes.data.notifications || []);
            setNotificationSummary(notifSummaryRes.data);
          } catch (notifErr) {
            console.warn("[AdminDashboard] Notifications endpoint not available:", notifErr?.response?.status);
          }
        } catch (authError) {
          console.warn("[AdminDashboard] Auth-protected endpoints inaccessible (admin required):", authError?.response?.status);
        }
      }
      
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({ title: "Error", description: "Failed to load data", variant: "destructive" });
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Nova AI Query
  const askNova = async () => {
    if (!novaQuestion.trim()) return;
    setNovaLoading(true);
    try {
      const response = await axios.post(`${API}/nova/query`, {
        question: novaQuestion,
        user_type: "admin"
      });
      setNovaResponse(response.data.response);
    } catch (error) {
      setNovaResponse("Error connecting to Nova. Please try again.");
    }
    setNovaLoading(false);
  };

  // Edit handlers
  const handleEditStartup = async () => {
    try {
      const updateData = {
        ...editItem,
        founders: typeof editItem.founders === 'string' ? editItem.founders.split(",").map(f => f.trim()) : editItem.founders,
        governance_score: parseFloat(editItem.governance_score)
      };
      await axios.put(`${API}/startups/${editItem.id}`, updateData);
      toast({ title: "Success", description: "Startup updated successfully" });
      setEditStartupOpen(false);
      setEditItem(null);
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update startup", variant: "destructive" });
    }
  };

  const handleEditMentor = async () => {
    try {
      await axios.put(`${API}/mentors/${editItem.id}`, editItem);
      toast({ title: "Success", description: "Mentor updated successfully" });
      setEditMentorOpen(false);
      setEditItem(null);
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update mentor", variant: "destructive" });
    }
  };

  const handleEditFounder = async () => {
    try {
      await axios.put(`${API}/founders/${encodeURIComponent(editItem.original_email || editItem.email)}`, editItem);
      toast({ title: "Success", description: "Founder updated successfully" });
      setEditFounderOpen(false);
      setEditItem(null);
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update founder", variant: "destructive" });
    }
  };

  // Export data
  const exportData = async (dataType) => {
    try {
      const response = await axios.post(`${API}/admin/export/${dataType}`);
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ski_axis_${dataType}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast({ title: "Exported", description: `${response.data.count} ${dataType} exported` });
    } catch (error) {
      toast({ title: "Error", description: "Export failed", variant: "destructive" });
    }
  };

  // Update company status
  const updateCompanyStatus = async (companyId, status, paymentStatus) => {
    try {
      await axios.put(`${API}/companies/${companyId}/status`, { status, payment_status: paymentStatus });
      toast({ title: "Updated", description: "Company status updated" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  // Approval handlers
  const handleMentorApproval = async (registrationId, status) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/mentor-registrations/${registrationId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: `Mentor registration ${status}` });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleInvestorApproval = async (investorId, status) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/investors/${investorId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: `Investor ${status}` });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleUserApproval = async (userId, approved) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/auth/users/${userId}/approve?approved=${approved}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: `User ${approved ? 'approved' : 'rejected'}` });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handlePitchBookingApproval = async (bookingId, status) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/pitch-bookings/${bookingId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: `Pitch booking ${status}` });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  // CRUD Operations
  const handleAddStartup = async () => {
    try {
      const startupData = {
        ...newStartup,
        founders: newStartup.founders.split(",").map(f => f.trim()),
        governance_score: parseFloat(newStartup.governance_score),
        progress: 50,
        jobs_created: 5,
        revenue_stage: "$0",
        funding_raised: "$0",
        sdg_alignment: []
      };
      await axios.post(`${API}/startups`, startupData);
      toast({ title: "Success", description: "Startup added successfully" });
      setAddStartupOpen(false);
      setNewStartup({
        name: "", founders: "", industry: "", stage: "MVP", location: "Dubai, UAE",
        description: "", governance_score: 7, compliance_status: "Yellow", 
        risk_level: "Medium", ecosystem: "Climate Tech", cohort: "Cohort 1", mentor: ""
      });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add startup", variant: "destructive" });
    }
  };

  const handleAddMentor = async () => {
    try {
      await axios.post(`${API}/mentors`, newMentor);
      toast({ title: "Success", description: "Mentor added successfully" });
      setAddMentorOpen(false);
      setNewMentor({ name: "", expertise: "", bio: "", email: "" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add mentor", variant: "destructive" });
    }
  };

  const handleAddFounder = async () => {
    try {
      // Add to both founders and recipients collections
      await axios.post(`${API}/founders/add`, newFounder);
      toast({ title: "Success", description: "Founder added successfully" });
      setAddFounderOpen(false);
      setNewFounder({ first_name: "", last_name: "", email: "", company: "", title: "", industry: "Technology" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add founder", variant: "destructive" });
    }
  };

  const handleDeleteStartup = async (id) => {
    try {
      await axios.delete(`${API}/startups/${id}`);
      toast({ title: "Deleted", description: "Startup removed successfully" });
      setDeleteConfirm(null);
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete startup", variant: "destructive" });
    }
  };

  const handleDeleteMentor = async (id) => {
    try {
      await axios.delete(`${API}/mentors/${id}`);
      toast({ title: "Deleted", description: "Mentor removed successfully" });
      setDeleteConfirm(null);
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete mentor", variant: "destructive" });
    }
  };

  const handleDeleteFounder = async (email) => {
    try {
      await axios.delete(`${API}/founders/${encodeURIComponent(email)}`);
      toast({ title: "Deleted", description: "Founder removed successfully" });
      setDeleteConfirm(null);
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete founder", variant: "destructive" });
    }
  };

  // Filter functions
  const filteredStartups = startups.filter(s => 
    s.name?.toLowerCase().includes(startupSearch.toLowerCase()) ||
    s.industry?.toLowerCase().includes(startupSearch.toLowerCase())
  );

  const filteredFounders = founders.filter(f => 
    f.first_name?.toLowerCase().includes(founderSearch.toLowerCase()) ||
    f.last_name?.toLowerCase().includes(founderSearch.toLowerCase()) ||
    f.company?.toLowerCase().includes(founderSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#C8A84B] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#3A4A5C] text-lg">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      {/* Header */}
      <header className="bg-[#FAF7F0]/92 backdrop-blur-xl shadow-sm border-b border-[rgba(200,168,75,0.25)] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/")}
                className="text-[#3A4A5C] hover:text-[#0D1B2A] hover:bg-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Home
              </Button>
              <div className="h-8 w-px bg-[#F4EFE6]"></div>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-[#C8A84B] to-[#D9BC6A] p-2 rounded-xl">
                  <Settings className="h-6 w-6 text-[#0D1B2A]" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-[#0D1B2A]">Admin Control Panel</h1>
                  <p className="text-sm text-[#3A4A5C]">CEO Dashboard</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={fetchAllData}
                disabled={refreshing}
                className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C] hover:text-[#0D1B2A]"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <div className="flex items-center gap-2">
                <ArrowLeft className="h-5 w-5 text-[#C8A84B]" />
                <span className="text-lg font-bold text-[#FAF7F0]">SKI AXIS</span>
                <ArrowRight className="h-5 w-5 text-[#C8A84B]" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border-[rgba(13,27,42,0.1)] mb-8 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <BarChart3 className="h-4 w-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="approvals" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <CheckCircle className="h-4 w-4 mr-2" />Approvals
            </TabsTrigger>
            <TabsTrigger value="startups" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <Building2 className="h-4 w-4 mr-2" />Startups ({startups.length})
            </TabsTrigger>
            <TabsTrigger value="mentors" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <GraduationCap className="h-4 w-4 mr-2" />Mentors ({mentors.length})
            </TabsTrigger>
            <TabsTrigger value="founders" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <Users className="h-4 w-4 mr-2" />Founders ({founders.length})
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <Building className="h-4 w-4 mr-2" />Applications ({companies.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <PieChart className="h-4 w-4 mr-2" />Analytics
            </TabsTrigger>
            <TabsTrigger value="nova" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <MessageSquare className="h-4 w-4 mr-2" />Nova AI
            </TabsTrigger>
            <TabsTrigger value="mlops" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A]">
              <Cpu className="h-4 w-4 mr-2" />MLOps
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#C8A84B] data-[state=active]:text-[#07070A] relative">
              <Activity className="h-4 w-4 mr-2" />Notifications
              {notificationSummary?.pending?.total > 0 && (
                <Badge className="absolute -top-1 -right-1 bg-red-500 text-[#0D1B2A] text-xs px-1.5 py-0.5 min-w-5 h-5 flex items-center justify-center">
                  {notificationSummary.pending.total}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-white border border-[rgba(200,168,75,0.25)] text-[#0D1B2A]">
                <CardHeader className="pb-2">
                  <CardDescription className="text-[#C8A84B]">Total Startups</CardDescription>
                  <CardTitle className="text-5xl font-bold">{stats.total_startups || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-[#C8A84B]">
                    <Building2 className="h-4 w-4" />
                    <span className="text-sm">In Portfolio</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-[rgba(200,168,75,0.3)] text-[#0D1B2A]">
                <CardHeader className="pb-2">
                  <CardDescription className="text-[#C8A84B]">Total Mentors</CardDescription>
                  <CardTitle className="text-5xl font-bold">{stats.total_mentors || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-[#C8A84B]">
                    <GraduationCap className="h-4 w-4" />
                    <span className="text-sm">Expert Advisors</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-[rgba(200,168,75,0.3)] text-[#0D1B2A]">
                <CardHeader className="pb-2">
                  <CardDescription className="text-[#C8A84B]">Founder Contacts</CardDescription>
                  <CardTitle className="text-5xl font-bold">{founders.length}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-[#C8A84B]">
                    <Users className="h-4 w-4" />
                    <span className="text-sm">For Outreach</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-[rgba(200,168,75,0.3)] text-[#0D1B2A]">
                <CardHeader className="pb-2">
                  <CardDescription className="text-[#C8A84B]">Jobs Created</CardDescription>
                  <CardTitle className="text-5xl font-bold">{stats.total_jobs_created || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-[#C8A84B]">
                    <Briefcase className="h-4 w-4" />
                    <span className="text-sm">Total Impact</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-white border-[rgba(13,27,42,0.1)] mb-8">
              <CardHeader>
                <CardTitle className="text-[#0D1B2A] text-xl font-bold">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button onClick={() => { setActiveTab("startups"); setAddStartupOpen(true); }} className="h-20 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-2">
                    <Plus className="h-6 w-6" />
                    <span>Add Startup</span>
                  </Button>
                  <Button onClick={() => { setActiveTab("mentors"); setAddMentorOpen(true); }} className="h-20 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-2">
                    <UserPlus className="h-6 w-6" />
                    <span>Add Mentor</span>
                  </Button>
                  <Button onClick={() => { setActiveTab("founders"); setAddFounderOpen(true); }} className="h-20 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-2">
                    <UserPlus className="h-6 w-6" />
                    <span>Add Founder</span>
                  </Button>
                  <Button onClick={() => navigate("/email-campaign")} className="h-20 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-2">
                    <Mail className="h-6 w-6" />
                    <span>Send Campaign</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Ecosystem Breakdown */}
            <Card className="bg-white border-[rgba(13,27,42,0.1)]">
              <CardHeader>
                <CardTitle className="text-[#0D1B2A] text-xl font-bold">Ecosystem Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(stats.ecosystems || {}).map(([key, count]) => (
                    <div key={key} className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                      <div className="flex justify-between items-center">
                        <span className="text-[#3A4A5C] capitalize">{key.replace(/_/g, ' ')}</span>
                        <Badge className="bg-[#C8A84B] text-[#07070A]">{count}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals">
            <div className="space-y-6">
              {/* Mentor Registrations */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-orange-500" />
                    Mentor Masterclass Registrations
                  </CardTitle>
                  <CardDescription className="text-[#3A4A5C]">
                    Approve or reject mentor applications for the Masterclass program
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {mentorRegistrations.length === 0 ? (
                    <p className="text-[#3A4A5C] text-center py-8">No mentor registrations yet</p>
                  ) : (
                    <div className="space-y-4">
                      {mentorRegistrations.map((reg) => (
                        <div key={reg.id} className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="text-[#0D1B2A] font-semibold">{reg.name}</h4>
                                <Badge className={
                                  reg.status === 'approved' ? 'bg-green-600' :
                                  reg.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'
                                }>
                                  {reg.status}
                                </Badge>
                              </div>
                              <p className="text-[#3A4A5C] text-sm">{reg.email}</p>
                              <p className="text-[#3A4A5C] text-sm mt-1">
                                <strong>Expertise:</strong> {reg.expertise} | 
                                <strong> Experience:</strong> {reg.years_experience || 'N/A'}
                              </p>
                              {reg.masterclass_topics?.length > 0 && (
                                <p className="text-[#3A4A5C] text-xs mt-1">
                                  Topics: {reg.masterclass_topics.join(', ')}
                                </p>
                              )}
                            </div>
                            {reg.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold"
                                  onClick={() => handleMentorApproval(reg.id, 'approved')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleMentorApproval(reg.id, 'rejected')}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Investor Registrations */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    Investor Registrations
                  </CardTitle>
                  <CardDescription className="text-[#3A4A5C]">
                    Review and approve investor network applications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {investors.length === 0 ? (
                    <p className="text-[#3A4A5C] text-center py-8">No investor registrations yet</p>
                  ) : (
                    <div className="space-y-4">
                      {investors.map((inv) => (
                        <div key={inv.id} className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="text-[#0D1B2A] font-semibold">{inv.name}</h4>
                                <Badge className={
                                  inv.status === 'approved' ? 'bg-green-600' :
                                  inv.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'
                                }>
                                  {inv.status}
                                </Badge>
                              </div>
                              <p className="text-[#3A4A5C] text-sm">{inv.email}</p>
                              <p className="text-[#3A4A5C] text-sm mt-1">
                                <strong>Type:</strong> {inv.investor_type} | 
                                <strong> Range:</strong> {inv.investment_range || 'N/A'}
                              </p>
                              {inv.sectors?.length > 0 && (
                                <p className="text-[#3A4A5C] text-xs mt-1">
                                  Sectors: {inv.sectors.join(', ')}
                                </p>
                              )}
                            </div>
                            {inv.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold"
                                  onClick={() => handleInvestorApproval(inv.id, 'approved')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleInvestorApproval(inv.id, 'rejected')}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Approvals */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    User Account Approvals
                  </CardTitle>
                  <CardDescription className="text-[#3A4A5C]">
                    Approve user accounts (Investors, Startups, Mentors need approval)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {users.filter(u => !u.is_approved && u.role !== 'admin').length === 0 ? (
                    <p className="text-[#3A4A5C] text-center py-8">No pending user approvals</p>
                  ) : (
                    <div className="space-y-4">
                      {users.filter(u => !u.is_approved && u.role !== 'admin').map((user) => (
                        <div key={user.id} className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="text-[#0D1B2A] font-semibold">{user.full_name}</h4>
                                <Badge className={
                                  user.role === 'founder' ? 'bg-green-600' :
                                  user.role === 'investor' ? 'bg-blue-600' :
                                  user.role === 'startup' ? 'bg-purple-600' : 'bg-orange-600'
                                }>
                                  {user.role}
                                </Badge>
                              </div>
                              <p className="text-[#3A4A5C] text-sm">{user.email}</p>
                              {user.company_name && (
                                <p className="text-[#3A4A5C] text-sm mt-1">Company: {user.company_name}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold"
                                onClick={() => handleUserApproval(user.id, true)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleUserApproval(user.id, false)}
                              >
                                <XCircle className="h-4 w-4 mr-1" />Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pitch Bookings */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-[#C8A84B]" />
                    Pitch Bookings
                  </CardTitle>
                  <CardDescription className="text-[#3A4A5C]">
                    Review and approve pitch session bookings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pitchBookings.length === 0 ? (
                    <p className="text-[#3A4A5C] text-center py-8">No pitch bookings yet</p>
                  ) : (
                    <div className="space-y-4">
                      {pitchBookings.map((booking) => (
                        <div key={booking.id} className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="text-[#0D1B2A] font-semibold">{booking.startup_name}</h4>
                                <Badge className={
                                  booking.status === 'approved' ? 'bg-green-600' :
                                  booking.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'
                                }>
                                  {booking.status}
                                </Badge>
                              </div>
                              <p className="text-[#3A4A5C] text-sm">{booking.founder_email}</p>
                              <p className="text-[#3A4A5C] text-sm mt-1">
                                <strong>Date:</strong> {new Date(booking.preferred_date).toLocaleDateString()} | 
                                <strong> Time:</strong> {booking.preferred_time}
                              </p>
                              {booking.pitch_deck_url && (
                                <p className="text-[#3A4A5C] text-xs mt-1">
                                  <a href={booking.pitch_deck_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                                    View Pitch Deck
                                  </a>
                                </p>
                              )}
                            </div>
                            {booking.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold"
                                  onClick={() => handlePitchBookingApproval(booking.id, 'approved')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handlePitchBookingApproval(booking.id, 'rejected')}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Startups Tab */}
          <TabsContent value="startups">
            <Card className="bg-white border-[rgba(13,27,42,0.1)]">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[#C8A84B]" />
                    Manage Startups
                  </CardTitle>
                  <div className="flex gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3A4A5C]" />
                      <Input 
                        placeholder="Search startups..."
                        value={startupSearch}
                        onChange={(e) => setStartupSearch(e.target.value)}
                        className="pl-10 bg-[#FAF7F0] border-[rgba(13,27,42,0.15)] text-[#0D1B2A] w-48"
                      />
                    </div>
                    <Dialog open={addStartupOpen} onOpenChange={setAddStartupOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                          <Plus className="h-4 w-4 mr-2" />Add Startup
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white border-[rgba(13,27,42,0.1)] text-[#0D1B2A] max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Add New Startup</DialogTitle>
                          <DialogDescription className="text-[#3A4A5C]">Add a startup to the portfolio</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-4 py-4">
                          <div>
                            <Label>Startup Name *</Label>
                            <Input value={newStartup.name} onChange={(e) => setNewStartup({...newStartup, name: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div>
                            <Label>Founders (comma separated) *</Label>
                            <Input value={newStartup.founders} onChange={(e) => setNewStartup({...newStartup, founders: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" placeholder="John Doe, Jane Smith" />
                          </div>
                          <div>
                            <Label>Industry *</Label>
                            <Input value={newStartup.industry} onChange={(e) => setNewStartup({...newStartup, industry: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div>
                            <Label>Stage</Label>
                            <Select value={newStartup.stage} onValueChange={(v) => setNewStartup({...newStartup, stage: v})}>
                              <SelectTrigger className="bg-white border-[rgba(13,27,42,0.15)]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="MVP">MVP</SelectItem>
                                <SelectItem value="Early Revenue">Early Revenue</SelectItem>
                                <SelectItem value="Growth">Growth</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Ecosystem</Label>
                            <Select value={newStartup.ecosystem} onValueChange={(v) => setNewStartup({...newStartup, ecosystem: v})}>
                              <SelectTrigger className="bg-white border-[rgba(13,27,42,0.15)]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Climate Tech">Climate Tech</SelectItem>
                                <SelectItem value="FinTech / RegTech">FinTech / RegTech</SelectItem>
                                <SelectItem value="Cross-Border Digital Services">Cross-Border Services</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Governance Score (1-10)</Label>
                            <Input type="number" min="1" max="10" value={newStartup.governance_score} onChange={(e) => setNewStartup({...newStartup, governance_score: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div>
                            <Label>Compliance Status</Label>
                            <Select value={newStartup.compliance_status} onValueChange={(v) => setNewStartup({...newStartup, compliance_status: v})}>
                              <SelectTrigger className="bg-white border-[rgba(13,27,42,0.15)]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Green">Green</SelectItem>
                                <SelectItem value="Yellow">Yellow</SelectItem>
                                <SelectItem value="Red">Red</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Risk Level</Label>
                            <Select value={newStartup.risk_level} onValueChange={(v) => setNewStartup({...newStartup, risk_level: v})}>
                              <SelectTrigger className="bg-white border-[rgba(13,27,42,0.15)]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Low">Low</SelectItem>
                                <SelectItem value="Medium">Medium</SelectItem>
                                <SelectItem value="High">High</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Label>Description</Label>
                            <Textarea value={newStartup.description} onChange={(e) => setNewStartup({...newStartup, description: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setAddStartupOpen(false)} className="border-[rgba(13,27,42,0.15)]">Cancel</Button>
                          <Button onClick={handleAddStartup} className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">Add Startup</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[rgba(200,168,75,0.25)]">
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Startup</th>
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Industry</th>
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Ecosystem</th>
                        <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Score</th>
                        <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Status</th>
                        <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStartups.map((startup, idx) => (
                        <tr key={startup.id || idx} className="border-b border-[rgba(200,168,75,0.25)]/50 hover:bg-[#F4EFE6]/30">
                          <td className="py-4 px-4">
                            <div>
                              <p className="text-[#0D1B2A] font-medium">{startup.name}</p>
                              <p className="text-[#3A4A5C] text-sm">{startup.founders?.join(", ")}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-[#3A4A5C]">{startup.industry}</td>
                          <td className="py-4 px-4">
                            <Badge variant="outline" className="text-[#3A4A5C] border-[rgba(13,27,42,0.15)]">{startup.ecosystem}</Badge>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className={`font-bold ${startup.governance_score >= 8 ? 'text-[#C8A84B]' : startup.governance_score >= 6 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {startup.governance_score}/10
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Badge className={startup.compliance_status === "Green" ? "bg-green-600" : startup.compliance_status === "Yellow" ? "bg-yellow-600" : "bg-red-600"}>
                              {startup.compliance_status}
                            </Badge>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-900/20" onClick={() => setDeleteConfirm({type: 'startup', id: startup.id, name: startup.name})}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mentors Tab */}
          <TabsContent value="mentors">
            <Card className="bg-white border-[rgba(13,27,42,0.1)]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-blue-500" />
                    Manage Mentors
                  </CardTitle>
                  <Dialog open={addMentorOpen} onOpenChange={setAddMentorOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                        <Plus className="h-4 w-4 mr-2" />Add Mentor
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-[rgba(13,27,42,0.1)] text-[#0D1B2A]">
                      <DialogHeader>
                        <DialogTitle>Add New Mentor</DialogTitle>
                        <DialogDescription className="text-[#3A4A5C]">Add an expert mentor to the network</DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div>
                          <Label>Full Name *</Label>
                          <Input value={newMentor.name} onChange={(e) => setNewMentor({...newMentor, name: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                        </div>
                        <div>
                          <Label>Expertise *</Label>
                          <Input value={newMentor.expertise} onChange={(e) => setNewMentor({...newMentor, expertise: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" placeholder="e.g., Energy Policy Expert" />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input value={newMentor.email} onChange={(e) => setNewMentor({...newMentor, email: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                        </div>
                        <div>
                          <Label>Bio</Label>
                          <Textarea value={newMentor.bio} onChange={(e) => setNewMentor({...newMentor, bio: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddMentorOpen(false)} className="border-[rgba(13,27,42,0.15)]">Cancel</Button>
                        <Button onClick={handleAddMentor} className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">Add Mentor</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mentors.map((mentor, idx) => (
                    <div key={mentor.id || idx} className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                      <div className="flex justify-between items-start mb-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#C8A84B] to-[#D9BC6A] flex items-center justify-center text-[#0D1B2A] font-bold">
                          {mentor.name?.split(' ').map(n => n[0]).join('')}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => setDeleteConfirm({type: 'mentor', id: mentor.id, name: mentor.name})}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <h3 className="text-[#0D1B2A] font-semibold">{mentor.name}</h3>
                      <p className="text-blue-400 text-sm mb-2">{mentor.expertise}</p>
                      <p className="text-[#3A4A5C] text-sm line-clamp-2">{mentor.bio}</p>
                      {mentor.email && <p className="text-[#7A8190] text-xs mt-2">{mentor.email}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Founders Tab */}
          <TabsContent value="founders">
            <Card className="bg-white border-[rgba(13,27,42,0.1)]">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    Manage Founders ({founders.length})
                  </CardTitle>
                  <div className="flex gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3A4A5C]" />
                      <Input 
                        placeholder="Search founders..."
                        value={founderSearch}
                        onChange={(e) => setFounderSearch(e.target.value)}
                        className="pl-10 bg-[#FAF7F0] border-[rgba(13,27,42,0.15)] text-[#0D1B2A] w-48"
                      />
                    </div>
                    <Dialog open={addFounderOpen} onOpenChange={setAddFounderOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                          <Plus className="h-4 w-4 mr-2" />Add Founder
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white border-[rgba(13,27,42,0.1)] text-[#0D1B2A]">
                        <DialogHeader>
                          <DialogTitle>Add New Founder</DialogTitle>
                          <DialogDescription className="text-[#3A4A5C]">Add a founder to the contact list</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-4 py-4">
                          <div>
                            <Label>First Name *</Label>
                            <Input value={newFounder.first_name} onChange={(e) => setNewFounder({...newFounder, first_name: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div>
                            <Label>Last Name *</Label>
                            <Input value={newFounder.last_name} onChange={(e) => setNewFounder({...newFounder, last_name: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div className="col-span-2">
                            <Label>Email *</Label>
                            <Input value={newFounder.email} onChange={(e) => setNewFounder({...newFounder, email: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div>
                            <Label>Company</Label>
                            <Input value={newFounder.company} onChange={(e) => setNewFounder({...newFounder, company: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div>
                            <Label>Title</Label>
                            <Input value={newFounder.title} onChange={(e) => setNewFounder({...newFounder, title: e.target.value})} className="bg-white border-[rgba(13,27,42,0.15)]" />
                          </div>
                          <div className="col-span-2">
                            <Label>Industry</Label>
                            <Select value={newFounder.industry} onValueChange={(v) => setNewFounder({...newFounder, industry: v})}>
                              <SelectTrigger className="bg-white border-[rgba(13,27,42,0.15)]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Technology">Technology</SelectItem>
                                <SelectItem value="Fintech">Fintech</SelectItem>
                                <SelectItem value="Banking">Banking</SelectItem>
                                <SelectItem value="Healthcare">Healthcare</SelectItem>
                                <SelectItem value="Real Estate">Real Estate</SelectItem>
                                <SelectItem value="Retail">Retail</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setAddFounderOpen(false)} className="border-[rgba(13,27,42,0.15)]">Cancel</Button>
                          <Button onClick={handleAddFounder} className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">Add Founder</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[rgba(200,168,75,0.25)]">
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Company</th>
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Title</th>
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Industry</th>
                        <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Email</th>
                        <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFounders.slice(0, 50).map((founder, idx) => (
                        <tr key={idx} className="border-b border-[rgba(200,168,75,0.25)]/50 hover:bg-[#F4EFE6]/30">
                          <td className="py-3 px-4">
                            <p className="text-[#0D1B2A] font-medium">{founder.first_name} {founder.last_name}</p>
                          </td>
                          <td className="py-3 px-4 text-[#3A4A5C]">{founder.company}</td>
                          <td className="py-3 px-4 text-[#3A4A5C] text-sm">{founder.title}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-[#3A4A5C] border-[rgba(13,27,42,0.15)]">{founder.industry}</Badge>
                          </td>
                          <td className="py-3 px-4 text-[#3A4A5C] text-sm">{founder.email}</td>
                          <td className="py-3 px-4 text-center">
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-900/20" onClick={() => setDeleteConfirm({type: 'founder', id: founder.email, name: `${founder.first_name} ${founder.last_name}`})}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredFounders.length > 50 && (
                    <p className="text-center text-[#7A8190] py-4">Showing 50 of {filteredFounders.length} founders</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card className="bg-white border-[rgba(13,27,42,0.1)]">
              <CardHeader>
                <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                  <Building className="h-5 w-5 text-orange-500" />
                  Company Applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <div className="text-center py-12 text-[#3A4A5C]">
                    <Building className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No applications yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[rgba(200,168,75,0.25)]">
                          <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Company</th>
                          <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Founder</th>
                          <th className="text-left py-3 px-4 text-[#3A4A5C] font-medium">Email</th>
                          <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Status</th>
                          <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Payment</th>
                          <th className="text-center py-3 px-4 text-[#3A4A5C] font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.map((company, idx) => (
                          <tr key={company.id || idx} className="border-b border-[rgba(200,168,75,0.25)]/50 hover:bg-[#F4EFE6]/30">
                            <td className="py-4 px-4 text-[#0D1B2A] font-medium">{company.name}</td>
                            <td className="py-4 px-4 text-[#3A4A5C]">{company.founder_name}</td>
                            <td className="py-4 px-4 text-[#3A4A5C]">{company.founder_email}</td>
                            <td className="py-4 px-4 text-center">
                              <Select 
                                value={company.status || "pending"} 
                                onValueChange={(v) => updateCompanyStatus(company.id, v, company.payment_status)}
                              >
                                <SelectTrigger className="w-28 bg-[#FAF7F0] border-[rgba(13,27,42,0.15)]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="accepted">Accepted</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Select 
                                value={company.payment_status || "pending"} 
                                onValueChange={(v) => updateCompanyStatus(company.id, company.status, v)}
                              >
                                <SelectTrigger className="w-28 bg-[#FAF7F0] border-[rgba(13,27,42,0.15)]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate(`/company/${company.id}`)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-[#C8A84B]" />
                    Compliance Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[#3A4A5C]">Green (Compliant)</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-[#F4EFE6] rounded-full h-3 overflow-hidden">
                            <div className="h-full bg-[#C8A84B]/80" style={{width: `${(analytics.compliance.green / stats.total_startups) * 100}%`}}></div>
                          </div>
                          <Badge className="bg-[#C8A84B] text-[#07070A]">{analytics.compliance.green}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#3A4A5C]">Yellow (In Progress)</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-[#F4EFE6] rounded-full h-3 overflow-hidden">
                            <div className="h-full bg-[#C8A84B]" style={{width: `${(analytics.compliance.yellow / stats.total_startups) * 100}%`}}></div>
                          </div>
                          <Badge className="bg-yellow-600">{analytics.compliance.yellow}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#3A4A5C]">Red (At Risk)</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-[#F4EFE6] rounded-full h-3 overflow-hidden">
                            <div className="h-full bg-red-500" style={{width: `${(analytics.compliance.red / stats.total_startups) * 100}%`}}></div>
                          </div>
                          <Badge className="bg-red-600">{analytics.compliance.red}</Badge>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-[rgba(13,27,42,0.1)]">
                        <p className="text-2xl font-bold text-[#C8A84B]">{analytics.compliance.rate}% Compliance Rate</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-[#C8A84B]" />
                    Revenue & Payments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[#3A4A5C]">Completed Payments</span>
                        <Badge className="bg-[#C8A84B] text-[#07070A]">{analytics.payments.completed}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#3A4A5C]">Pending Payments</span>
                        <Badge className="bg-yellow-600">{analytics.payments.pending}</Badge>
                      </div>
                      <div className="pt-4 border-t border-[rgba(13,27,42,0.1)]">
                        <p className="text-3xl font-bold text-[#C8A84B]">${analytics.payments.total_revenue}</p>
                        <p className="text-[#3A4A5C] text-sm">Total Revenue</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    Founder Industries
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics && (
                    <div className="space-y-2">
                      {Object.entries(analytics.founder_industries || {}).slice(0, 6).map(([industry, count]) => (
                        <div key={industry} className="flex justify-between items-center">
                          <span className="text-[#3A4A5C]">{industry}</span>
                          <Badge variant="outline" className="text-[#3A4A5C] border-[rgba(13,27,42,0.15)]">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                    <FileDown className="h-5 w-5 text-blue-500" />
                    Export Data
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => exportData('startups')} variant="outline" className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C]">
                      <Download className="h-4 w-4 mr-2" />Startups
                    </Button>
                    <Button onClick={() => exportData('mentors')} variant="outline" className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C]">
                      <Download className="h-4 w-4 mr-2" />Mentors
                    </Button>
                    <Button onClick={() => exportData('founders')} variant="outline" className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C]">
                      <Download className="h-4 w-4 mr-2" />Founders
                    </Button>
                    <Button onClick={() => exportData('companies')} variant="outline" className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C]">
                      <Download className="h-4 w-4 mr-2" />Companies
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Nova AI Tab */}
          <TabsContent value="nova">
            <Card className="bg-white border-[rgba(13,27,42,0.1)]">
              <CardHeader>
                <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#C8A84B]" />
                  Nova AI Assistant
                </CardTitle>
                <CardDescription className="text-[#3A4A5C]">
                  Ask Nova about startups, governance, analytics, or get help with any task
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <Input 
                      placeholder="Ask Nova anything... e.g., 'Show me Climate Tech startups' or 'What's our compliance rate?'"
                      value={novaQuestion}
                      onChange={(e) => setNovaQuestion(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && askNova()}
                      className="bg-white border-[rgba(13,27,42,0.15)] text-[#0D1B2A] flex-1"
                    />
                    <Button onClick={askNova} disabled={novaLoading} className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold">
                      {novaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {novaResponse && (
                    <div className="bg-[#F4EFE6] rounded-lg p-4 border border-[rgba(13,27,42,0.1)]">
                      <div className="flex items-start gap-3">
                        <div className="bg-green-600 p-2 rounded-lg">
                          <MessageSquare className="h-4 w-4 text-[#0D1B2A]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-[#C8A84B] font-medium mb-2">Nova</p>
                          <p className="text-[#3A4A5C] whitespace-pre-wrap">{novaResponse}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
                    <Button variant="outline" onClick={() => { setNovaQuestion("What's our total portfolio value?"); askNova(); }} className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C] text-sm">
                      Portfolio Value
                    </Button>
                    <Button variant="outline" onClick={() => { setNovaQuestion("List top performing startups by governance score"); askNova(); }} className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C] text-sm">
                      Top Performers
                    </Button>
                    <Button variant="outline" onClick={() => { setNovaQuestion("What's our compliance rate across ecosystems?"); askNova(); }} className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C] text-sm">
                      Compliance Stats
                    </Button>
                    <Button variant="outline" onClick={() => { setNovaQuestion("How many jobs have we created?"); askNova(); }} className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C] text-sm">
                      Jobs Impact
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MLOps Tab */}
          <TabsContent value="mlops">
            <div className="space-y-6">
              {/* Model Status */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-500/20 rounded-xl">
                        <Cpu className="h-8 w-8 text-purple-500" />
                      </div>
                      <div>
                        <h3 className="text-[#0D1B2A] font-semibold text-lg">{mlopsData?.current_model?.model_id || "nova-v1.0"}</h3>
                        <p className="text-[#3A4A5C]">{mlopsData?.current_model?.model_name || "gpt-4o-mini"} • v{mlopsData?.current_model?.version || "1.0.0"}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-600 text-[#0D1B2A] px-4 py-1">
                      <CheckCircle className="h-4 w-4 mr-1" />Production
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                  <CardContent className="p-4 text-center">
                    <Activity className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-[#0D1B2A]">{mlopsData?.metrics?.total_inferences || 0}</p>
                    <p className="text-xs text-[#3A4A5C]">Total Inferences</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="h-6 w-6 text-[#C8A84B] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-[#0D1B2A]">{mlopsData?.metrics?.success_rate || 100}%</p>
                    <p className="text-xs text-[#3A4A5C]">Success Rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                  <CardContent className="p-4 text-center">
                    <Clock className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-[#0D1B2A]">{mlopsData?.metrics?.avg_latency_ms || 0}ms</p>
                    <p className="text-xs text-[#3A4A5C]">Avg Latency</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="h-6 w-6 text-[#C8A84B] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-[#0D1B2A]">${mlopsData?.metrics?.total_cost_usd || 0}</p>
                    <p className="text-xs text-[#3A4A5C]">Total Cost</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                  <CardHeader>
                    <CardTitle className="text-[#0D1B2A] text-xl font-bold">Daily Inference Volume</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {mlopsData?.daily_stats?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={mlopsData.daily_stats}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="_id" stroke="#9CA3AF" fontSize={10} />
                          <YAxis stroke="#9CA3AF" fontSize={10} />
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                          <Bar dataKey="calls" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-[#3A4A5C] text-center py-8">No data yet - make AI queries to generate data</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                  <CardHeader>
                    <CardTitle className="text-[#0D1B2A] text-xl font-bold">Cost Tracking ($)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {mlopsData?.daily_stats?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={mlopsData.daily_stats}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="_id" stroke="#9CA3AF" fontSize={10} />
                          <YAxis stroke="#9CA3AF" fontSize={10} />
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }} />
                          <Line type="monotone" dataKey="cost" stroke="#10B981" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-[#3A4A5C] text-center py-8">Cost data will appear here</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* System Info */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] text-xl font-bold">System Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="p-3 bg-[#F4EFE6] rounded-lg">
                      <p className="text-[#3A4A5C]">Provider</p>
                      <p className="text-[#0D1B2A] font-medium">OpenAI (Emergent)</p>
                    </div>
                    <div className="p-3 bg-[#F4EFE6] rounded-lg">
                      <p className="text-[#3A4A5C]">Model</p>
                      <p className="text-[#0D1B2A] font-medium">GPT-4o-mini</p>
                    </div>
                    <div className="p-3 bg-[#F4EFE6] rounded-lg">
                      <p className="text-[#3A4A5C]">Total Tokens</p>
                      <p className="text-[#0D1B2A] font-medium">{(mlopsData?.metrics?.total_tokens || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-[#F4EFE6] rounded-lg">
                      <p className="text-[#3A4A5C]">Deployment</p>
                      <p className="text-[#0D1B2A] font-medium">Healthy ✓</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="space-y-6">
              {/* Summary Cards */}
              {notificationSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-white border border-[rgba(200,168,75,0.3)] text-[#0D1B2A]">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold">{notificationSummary.pending?.investors || 0}</p>
                      <p className="text-sm opacity-90">Pending Investors</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border border-[rgba(200,168,75,0.3)] border-none text-[#0D1B2A]">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold">{notificationSummary.pending?.mentors || 0}</p>
                      <p className="text-sm opacity-90">Pending Mentors</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border border-[rgba(200,168,75,0.25)] border-none text-[#0D1B2A]">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold">{notificationSummary.pending?.pitch_bookings || 0}</p>
                      <p className="text-sm opacity-90">Pending Pitches</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border border-[rgba(200,168,75,0.3)] text-[#0D1B2A]">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold">{notificationSummary.today?.new_signups || 0}</p>
                      <p className="text-sm opacity-90">Today's Signups</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Recent Notifications */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[#0D1B2A] flex items-center gap-2">
                      <Activity className="h-5 w-5 text-blue-500" />
                      Recent Activity
                    </CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-[rgba(13,27,42,0.15)] text-[#3A4A5C]"
                      onClick={async () => {
                        const token = localStorage.getItem('token');
                        try {
                          await axios.put(`${API}/admin/notifications/mark-all-read`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast({ title: "Success", description: "All notifications marked as read" });
                          fetchAllData();
                        } catch (e) {
                          toast({ title: "Error", description: "Failed to mark notifications", variant: "destructive" });
                        }
                      }}
                    >
                      Mark All Read
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <p className="text-[#3A4A5C] text-center py-8">No recent activity</p>
                  ) : (
                    <div className="space-y-3">
                      {notifications.slice(0, 20).map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-4 rounded-lg border ${notif.is_read ? 'bg-[#FAF7F0]/30 border-[rgba(13,27,42,0.1)]' : 'bg-blue-900/20 border-blue-700'}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={
                                  notif.type === 'signup' ? 'bg-green-600' :
                                  notif.type === 'application' ? 'bg-blue-600' :
                                  notif.type === 'investor' ? 'bg-purple-600' :
                                  notif.type === 'mentor' ? 'bg-orange-600' : 'bg-gray-600'
                                }>
                                  {notif.type}
                                </Badge>
                                {!notif.is_read && <Badge className="bg-red-500 text-xs">New</Badge>}
                              </div>
                              <h4 className="text-[#0D1B2A] font-medium">{notif.title}</h4>
                              <p className="text-[#3A4A5C] text-sm">{notif.message}</p>
                              <p className="text-[#7A8190] text-xs mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                            </div>
                            {!notif.is_read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#3A4A5C] hover:text-white"
                                onClick={async () => {
                                  const token = localStorage.getItem('token');
                                  try {
                                    await axios.put(`${API}/admin/notifications/${notif.id}/read`, {}, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    fetchAllData();
                                  } catch (e) {}
                                }}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-white border-[rgba(13,27,42,0.1)]">
                <CardHeader>
                  <CardTitle className="text-[#0D1B2A] text-xl font-bold">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button 
                      onClick={() => setActiveTab("approvals")} 
                      className="h-16 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-1"
                    >
                      <CheckCircle className="h-5 w-5" />
                      <span className="text-sm">View Approvals</span>
                    </Button>
                    <Button 
                      onClick={() => navigate("/email-campaign")} 
                      className="h-16 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-1"
                    >
                      <Mail className="h-5 w-5" />
                      <span className="text-sm">Send Campaign</span>
                    </Button>
                    <Button 
                      onClick={() => setActiveTab("applications")} 
                      className="h-16 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-1"
                    >
                      <Building className="h-5 w-5" />
                      <span className="text-sm">Applications</span>
                    </Button>
                    <Button 
                      onClick={fetchAllData} 
                      className="h-16 bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold flex-col gap-1"
                    >
                      <RefreshCw className="h-5 w-5" />
                      <span className="text-sm">Refresh Data</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white border-[rgba(13,27,42,0.1)] text-[#0D1B2A]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirm Delete</DialogTitle>
            <DialogDescription className="text-[#3A4A5C]">
              Are you sure you want to delete <span className="text-[#0D1B2A] font-semibold">{deleteConfirm?.name}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-[rgba(13,27,42,0.15)]">Cancel</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteConfirm?.type === 'startup') handleDeleteStartup(deleteConfirm.id);
                else if (deleteConfirm?.type === 'mentor') handleDeleteMentor(deleteConfirm.id);
                else if (deleteConfirm?.type === 'founder') handleDeleteFounder(deleteConfirm.id);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#FAF7F0] border-t border-gray-800 py-6 mt-12">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ArrowLeft className="h-5 w-5 text-[#C8A84B]" />
            <span className="text-lg font-bold text-[#FAF7F0]">SKI AXIS</span>
            <ArrowRight className="h-5 w-5 text-[#C8A84B]" />
          </div>
          <p className="text-[#7A8190] text-sm">Admin Control Panel - CEO Access Only</p>
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;
